import { type ReactNode, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { HighlightedCode, useDocumentTheme, useHighlightedCommands } from './highlighted-command';

interface EditableCommandListProps {
  /** The editable commands. The parent owns this list. */
  commands: string[];
  /** Called with the next list whenever a command is added, edited, or removed. */
  onCommandsChange: (commands: string[]) => void;
  /** Placeholder for the add-command input. */
  placeholder?: string;
  /** Accessible label for the add button. Defaults to "Add command". */
  addLabel?: string;
  /** Accessible label for per-row remove buttons. Defaults to "Remove command". */
  removeLabel?: string;
  /** Shown in place of the list when there are no commands yet. */
  emptyHint?: ReactNode;
  /**
   * Controlled value for the add-command draft input. Provide together with
   * `onDraftChange` when the parent needs the unsaved draft (e.g. to include a
   * half-typed command when committing a save). When omitted, the draft is
   * managed internally.
   */
  draft?: string;
  onDraftChange?: (draft: string) => void;
  className?: string;
}

/**
 * Editable list of shell commands rendered as click-to-edit rows with an
 * add-row beneath. Each row reveals an inline input on click (Enter commits,
 * Escape cancels, blur commits, an emptied value removes the row) and a
 * remove button. The add-row appends on Enter or the Plus button.
 *
 * The editable counterpart to {@link CommandBlock}. The parent owns the
 * committed `commands` array via `onCommandsChange`; the draft for the
 * add-input can be controlled (`draft` + `onDraftChange`) when the unsaved
 * value must survive a save action, otherwise it is managed internally.
 */
export function EditableCommandList({
  commands,
  onCommandsChange,
  placeholder = 'apt-get install -y mycli',
  addLabel = 'Add command',
  removeLabel = 'Remove command',
  emptyHint,
  draft,
  onDraftChange,
  className,
}: EditableCommandListProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState('');
  const [internalDraft, setInternalDraft] = useState('');

  const theme = useDocumentTheme();
  const tokensByCommand = useHighlightedCommands(commands, theme);

  const draftControlled = draft !== undefined && onDraftChange !== undefined;
  const draftValue = draftControlled ? draft : internalDraft;
  const setDraftValue = (value: string) => {
    if (draftControlled) onDraftChange(value);
    else setInternalDraft(value);
  };

  function add() {
    const trimmed = draftValue.trim();
    if (!trimmed) return;
    onCommandsChange([...commands, trimmed]);
    setDraftValue('');
  }

  function commitEdit(index: number) {
    const trimmed = editingValue.trim();
    if (trimmed) {
      onCommandsChange(commands.map((cmd, i) => (i === index ? trimmed : cmd)));
    } else {
      onCommandsChange(commands.filter((_, i) => i !== index));
    }
    setEditingIndex(null);
    setEditingValue('');
  }

  function remove(index: number) {
    onCommandsChange(commands.filter((_, i) => i !== index));
  }

  return (
    <div className={cn('space-y-2', className)}>
      {commands.length > 0 ? (
        <div className="space-y-1">
          {commands.map((cmd, i) =>
            editingIndex === i ? (
              <div
                key={`${i}:${cmd}`}
                className="flex items-center gap-2 rounded border border-ring bg-muted/30 px-2 py-1 ring-[3px] ring-ring/50"
              >
                <span
                  aria-hidden
                  className="shrink-0 select-none font-mono text-xs leading-relaxed text-muted-foreground/40"
                >
                  $
                </span>
                <Input
                  autoFocus
                  value={editingValue}
                  onChange={(e) => setEditingValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEdit(i);
                    } else if (e.key === 'Escape') {
                      setEditingIndex(null);
                      setEditingValue('');
                    }
                  }}
                  onBlur={() => commitEdit(i)}
                  className="h-auto flex-1 border-0 bg-transparent p-0 font-mono text-xs leading-relaxed shadow-none focus-visible:ring-0 md:text-xs"
                />
              </div>
            ) : (
              <div
                key={`${i}:${cmd}`}
                className="flex items-center gap-2 rounded border border-border bg-muted/30 px-2 py-1.5"
              >
                <span
                  aria-hidden
                  className="shrink-0 select-none font-mono text-xs leading-relaxed text-muted-foreground/40"
                >
                  $
                </span>
                <button
                  type="button"
                  // The command renders as syntax-highlighted token spans, so it
                  // has no single matchable text node — the raw command is the
                  // button's accessible name for both screen readers and tests.
                  aria-label={cmd}
                  className={cn(
                    'min-w-0 flex-1 cursor-text truncate text-left font-mono text-xs leading-relaxed',
                    !tokensByCommand[i] && 'text-foreground/90',
                  )}
                  onClick={() => {
                    setEditingIndex(i);
                    setEditingValue(cmd);
                  }}
                >
                  {tokensByCommand[i] ? <HighlightedCode tokens={tokensByCommand[i]} /> : cmd}
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  className="shrink-0 p-0.5 text-muted-foreground hover:text-foreground"
                  aria-label={removeLabel}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ),
          )}
        </div>
      ) : (
        emptyHint != null && <div className="text-sm text-muted-foreground">{emptyHint}</div>
      )}
      <div className="flex items-center gap-2">
        <Input
          value={draftValue}
          onChange={(e) => setDraftValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="h-8 flex-1 px-2.5 font-mono text-xs leading-relaxed md:text-xs"
        />
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0"
          disabled={!draftValue.trim()}
          onClick={add}
          aria-label={addLabel}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
